import React from 'react'
import { AnalyticsMetricType } from 'src/lib/types/analytics'
import { ArrowUpRight } from 'react-feather'
import { classNames, numberWithCommas } from 'src/lib/utils/helpers'
import { secondsToDaysHoursMinutes } from 'src/lib/utils/helpers'

interface GenericMetricDataProps {
  widgetData: AnalyticsMetricType
  type: 'PRIMARY' | 'SECONDARY' | 'LIST'
}

const GenericMetricData: React.FC<GenericMetricDataProps> = ({ widgetData, type }) => {
  const hasComparison = Boolean(
    widgetData?.compareValue !== undefined &&
      widgetData?.comparePercentage !== undefined &&
      widgetData?.compareStatus !== undefined
  )

  const getWidgetValue = () => {
    if (!Boolean(widgetData?.value)) return '-'
    switch (widgetData?.type) {
      case 'AMOUNT':
        return '₹' + numberWithCommas(widgetData?.value?.toString())
      case 'PERCENTAGE':
        return numberWithCommas(widgetData?.value?.toString()) + '%'
      case 'TIME':
        return secondsToDaysHoursMinutes(Number.parseInt(widgetData?.value?.toString()))
      default:
        return widgetData?.value?.toString()
    }
  }

  switch (type) {
    case 'PRIMARY':
      return (
        <div className='flex flex-col gap-6 justify-between'>
          {Boolean(widgetData?.header) && <h2 className='text-base font-medium text-carbon'>{widgetData?.header}</h2>}
          <div className='flex flex-col gap-1'>
            <div className='flex flex-row gap-2 items-end'>
              <h2 className='text-[1.875rem] leading-7 text-coal-dark font-semibold'>{getWidgetValue()}</h2>
              {hasComparison && (
                <div
                  className={`flex flex-row gap-0.5 font-semibold ${
                    widgetData?.compareStatus === 'increased'
                      ? 'text-green-dark'
                      : widgetData?.compareStatus === 'decreased'
                      ? 'text-red-dark'
                      : 'text-gray-dark'
                  }`}
                >
                  <ArrowUpRight
                    strokeWidth={2.5}
                    className={classNames(
                      'w-4 h-4',
                      widgetData?.compareStatus === 'decreased' ? 'rotate-90' : '',
                      widgetData?.compareStatus === 'equal' ? 'hidden' : ''
                    )}
                  />
                  <span className='text-sm'>{widgetData?.comparePercentage}%</span>
                </div>
              )}
            </div>
            <p className='text-sm font-normal text-gray-dark'>{widgetData.title}</p>
          </div>
        </div>
      )
    case 'SECONDARY':
      return (
        <div className='flex flex-col gap-1'>
          <div className='flex flex-row gap-2 items-end'>
            <h2 className='text-lg leading-5 text-coal-dark font-semibold'>{getWidgetValue()}</h2>
            {hasComparison && (
              <div
                className={`flex flex-row gap-0.5 font-semibold ${
                  widgetData?.compareStatus === 'increased'
                    ? 'text-green-dark'
                    : widgetData?.compareStatus === 'decreased'
                    ? 'text-red-dark'
                    : 'text-gray-dark'
                }`}
              >
                <ArrowUpRight
                  strokeWidth={2.5}
                  className={classNames(
                    'w-4 h-4',
                    widgetData?.compareStatus === 'decreased' ? 'rotate-90' : '',
                    widgetData?.compareStatus === 'equal' ? 'hidden' : ''
                  )}
                />
                <span className='text-sm '>{widgetData?.comparePercentage}%</span>
              </div>
            )}
          </div>
          <p className='text-sm font-normal text-gray-dark'>{widgetData.title}</p>
        </div>
      )
    case 'LIST':
      return (
        <div className='flex flex-row gap-1 justify-between items-start'>
          <div className='flex flex-col'>
            <p className='text-sm font-normal text-coal-dark'>{widgetData.title}</p>
            {widgetData?.description !== undefined && (
              <span className='text-gray-dark text-sm font-normal'>{widgetData?.description}</span>
            )}
          </div>
          <div className='flex flex-row gap-2 items-end'>
            <p className='text-sm text-coal-dark font-medium'>{getWidgetValue()}</p>
            {hasComparison && (
              <div
                className={`flex flex-row gap-0.5 font-semibold ${
                  widgetData?.compareStatus === 'increased'
                    ? 'text-green-dark'
                    : widgetData?.compareStatus === 'decreased'
                    ? 'text-red-dark'
                    : 'text-gray-dark'
                }`}
              >
                <ArrowUpRight
                  strokeWidth={2.5}
                  className={classNames(
                    'w-4 h-4',
                    widgetData?.compareStatus === 'decreased' ? 'rotate-90' : '',
                    widgetData?.compareStatus === 'equal' ? 'hidden' : ''
                  )}
                />
                <span className='text-sm'>{widgetData?.comparePercentage}%</span>
              </div>
            )}
          </div>
        </div>
      )
    default:
      return <></>
  }
}

export default GenericMetricData
